import React, {ReactElement} from 'react';
import MComponent from '../Toolkit/Components/Component';
import {Column} from 'primereact/column';
import MTable, {MTableFilters} from '../Toolkit/Components/Controls/Table';
import MToolbar from '../Toolkit/Components/Controls/Toolbar';
import {DataTableSortMeta} from 'primereact/datatable';
import NSwagAdapter from '../Toolkit/Api/NSwagAdapter';
import MNSwagClientConfiguration from '../Toolkit/Api/NSwagClientConfiguration';
import TrafficManagementAccessTokenProvider from '../Api/TrafficManagementAccessTokenProvider';
import {IMsalContext} from '@azure/msal-react';
import {FilterMatchMode} from 'primereact/api';
import {MNullable} from '../Toolkit/Utils';
import {CrewClient} from '../Api/CrewClient';
import {CrewContactListReply, CrewContactData} from '../Api/ControllerTypes/CrewTypes';
import EnumContext from '../Context/enum-context';

type CrewRow = {
    data: CrewContactData;
}

type Props = {
    parentId: string;
    selectionOnly: boolean;
    crewTypes: number[];
    onSelectionChange?: (newSelection: CrewContactData | CrewContactData[] | undefined) => void;
    msalContext: IMsalContext;
}

/** */
type State = {
    filters: MTableFilters;
    selectedRow?: CrewRow;
    rowData: CrewRow[];
}

class CrewListControl extends MComponent<Props, State> {
    static contextType = EnumContext;
    context!: React.ContextType<typeof EnumContext>;

    private readonly m_crewClient: NSwagAdapter<CrewClient>;

    public constructor(props: Props) {
        super(props);

        this.m_crewClient = new NSwagAdapter<CrewClient>(new CrewClient(
            new MNSwagClientConfiguration(process.env.REACT_APP_TRAFFICMANAGEMENT_API_KEY!,
                new TrafficManagementAccessTokenProvider(this.props.msalContext))));

        this.bindMethods();
        this.initializeState();
    }

    private bindMethods(): void {
        this.table_onSelectionChange = this.table_onSelectionChange.bind(this);
        this.fileFilter_Change = this.fileFilter_Change.bind(this);
        this.crewTypeTemplate = this.crewTypeTemplate.bind(this);
    }

    private initializeState(): void {
        this.state = {
            filters: {},
            rowData: []
        };
    }

    async componentDidMount() {
        await this.loadPageData();
    }

    private table_onSelectionChange(selection: CrewRow | CrewRow[] | undefined): void {
        if(this.props.onSelectionChange !== undefined) {
            if(selection === undefined) {
                this.props.onSelectionChange(undefined);
            } else if (Array.isArray(selection)) {
                this.props.onSelectionChange((selection as CrewRow[]).map(s => s.data));
            } else {
                this.props.onSelectionChange((selection as CrewRow).data);
            }
        }
    }

    private async fileFilter_Change(newValue: MNullable<number>): Promise<void> {
        this.safeSetState(draft =>
            draft.filters = {
                'data.fileFolder': {
                    value: newValue === -1 ? null : newValue!,
                    matchMode: FilterMatchMode.EQUALS
                }
            });
    }

    private async loadPageData(): Promise<void> {

        //Read file data.
        const crewResult = await this.m_crewClient.callWithReply(
            CrewContactListReply, (client) => client.getCrewsOfType(this.props.crewTypes));
        crewResult.throwIfFailed();

        this.safeSetState(draft => {
            draft.rowData = crewResult.reply!.crews.filter(c => c.crew.active).map(c => ({data: c}));
        });
    }

    private tableSortTemplate(): DataTableSortMeta[] {
        return [
            {field: 'data.modifiedOn', order: -1}
        ];
    }

    private crewTypeTemplate(rowData: CrewRow): string | null {
        const index = this.context.enums?.crewTypes.findIndex(value => value.id === rowData.data.crew.crewType);

        if(index === undefined || index === -1) {
            return null;
        }

        return this.context.enums?.crewTypes[index!].name!;
    }

    private emailTemplate(rowData: CrewRow): ReactElement | null {
        return (
            <span style={{fontSize: 'small'}}>
                {rowData.data.crewContacts
                        .filter(cc => cc.contactType === 0)
                        .map(cc => <div>{cc.contact}</div>)}
            </span>
        );
    }

    public render(): ReactElement {
        return (
            <div>
                <MToolbar>

                </MToolbar>

                <MTable
                    rowKey="data.crew.id"
                    rowData={this.state.rowData}
                    filters={this.state.filters}
                    multipleSelection={this.props.selectionOnly}
                    onSelectionChange={this.table_onSelectionChange}
                    sortMode={'multiple'}
                    multiSortMeta={this.tableSortTemplate()}>

                    <Column field="data.crew.crewName"
                            header="Name"
                            filter={false}
                            sortable={false}
                            style={{width: '30%'}}/>
                    <Column field="data.crewType"
                            header="Type"
                            filter={false}
                            sortable={true}
                            body={this.crewTypeTemplate}
                            style={{width: '30%'}}/>
                    <Column header="contacts"
                            body={this.emailTemplate}
                            filter={false}
                            sortable={false}
                            style={{width: '40%'}}/>
                </MTable>
            </div>
        );
    }
}

export default CrewListControl;
